import React, { useState, useEffect, useCallback } from 'react';
import { fetchEvalRuns, fetchOptimizerTelemetry } from './evalTrendsApi';

const Card = ({ label, value, color = 'text-gray-900', sub }) => (
  <div className="bg-white rounded-lg border p-4">
    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);

const pct = (v, digits = 0) => (v === null || v === undefined ? '—' : `${(v * 100).toFixed(digits)}%`);

const OptimizerTelemetry = ({ data }) => {
  if (!data || !data.available) return null;
  const fr = data.fallback_rate;
  return (
    <div className="bg-white rounded-lg border p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        Optimizer fallback rate
        <span className="font-normal text-gray-400"> · last {data.window_days}d</span>
      </h3>
      {data.total === 0 ? (
        <div className="text-sm text-gray-400">No optimizer dispatches recorded in this window.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <Card label="Dispatches" value={data.total} />
            <Card label="Fell back to v1" value={data.fell_back}
                  color={data.fell_back ? 'text-yellow-600' : 'text-green-600'} />
            <Card label="Fallback rate" value={pct(fr, 1)}
                  color={fr >= 0.1 ? 'text-red-600' : fr > 0 ? 'text-yellow-600' : 'text-green-600'} />
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 text-xs">
                <th className="py-1 font-medium">Requested mode</th>
                <th className="font-medium">Dispatches</th>
                <th className="font-medium">Fell back</th>
                <th className="font-medium">Rate</th>
              </tr>
            </thead>
            <tbody>
              {data.by_mode.map((m) => (
                <tr key={m.requested_mode} className="border-t border-gray-50">
                  <td className="py-1.5 font-mono">{m.requested_mode}</td>
                  <td className="tabular-nums">{m.total}</td>
                  <td className="tabular-nums">{m.fell_back}</td>
                  <td className="tabular-nums">{pct(m.fallback_rate, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

function EvalTrends() {
  const [data, setData] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchEvalRuns('audit', 50));
      // Optimizer telemetry is supplementary — never fail the page on it.
      try {
        setTelemetry(await fetchOptimizerTelemetry(30));
      } catch {
        setTelemetry(null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-gray-500">Loading eval history…</div>;
  if (error) return <div className="text-red-600">Error: {error}</div>;

  if (!data.available) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Eval Trends</h2>
        <p className="text-gray-500">
          {data.note || 'The eval-results store is not present in this environment.'}
        </p>
      </div>
    );
  }

  const runs = data.runs; // newest first
  const latest = runs[0];
  const chrono = [...runs].reverse(); // oldest → newest for the bar chart

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Eval Trends — audit</h2>
          <div className="text-sm text-gray-500">{data.count} run(s) recorded · cross-institution</div>
        </div>
        <button onClick={load} className="px-3 py-1.5 text-sm bg-gray-100 rounded-md hover:bg-gray-200">
          Refresh
        </button>
      </div>

      {runs.length === 0 ? (
        <div className="bg-white rounded-lg border p-6 text-gray-500">
          No runs recorded yet. Eval runs append here automatically.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card
              label="Latest pass rate"
              value={pct(latest.pass_rate)}
              color={latest.pass_rate >= 1 ? 'text-green-600' : latest.pass_rate >= 0.8 ? 'text-yellow-600' : 'text-red-600'}
              sub={`${latest.scenarios_passed}/${latest.scenarios_run} scenarios`}
            />
            <Card label="Latest avg score" value={pct(latest.avg_overall, 1)} />
            <Card label="Latest recall" value={pct(latest.avg_recall)} sub={`precision ${pct(latest.avg_precision)}`} />
            <Card
              label="Avg audit time"
              value={latest.avg_time_ms === null ? '—' : `${Math.round(latest.avg_time_ms)}ms`}
            />
          </div>

          <div className="bg-white rounded-lg border p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Pass rate over time</h3>
            <div className="flex items-end gap-1 h-24" role="img" aria-label="pass rate history">
              {chrono.map((r) => (
                <div
                  key={r.id}
                  title={`${new Date(r.run_at).toLocaleString()}: ${pct(r.pass_rate)} (${r.scenarios_passed}/${r.scenarios_run})`}
                  className={`flex-1 rounded-t ${r.pass_rate >= 1 ? 'bg-green-400' : r.pass_rate >= 0.8 ? 'bg-yellow-400' : 'bg-red-400'}`}
                  style={{ height: `${Math.max(3, r.pass_rate * 100)}%` }}
                />
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent runs</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 text-xs">
                  <th className="py-1 font-medium">When</th>
                  <th className="font-medium">Pass</th>
                  <th className="font-medium">Avg score</th>
                  <th className="font-medium">Recall</th>
                  <th className="font-medium">Precision</th>
                  <th className="font-medium">git</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t border-gray-50">
                    <td className="py-1.5 text-gray-700">{new Date(r.run_at).toLocaleString()}</td>
                    <td className="tabular-nums">{r.scenarios_passed}/{r.scenarios_run}</td>
                    <td className="tabular-nums">{pct(r.avg_overall, 1)}</td>
                    <td className="tabular-nums">{pct(r.avg_recall)}</td>
                    <td className="tabular-nums">{pct(r.avg_precision)}</td>
                    <td className="font-mono text-xs text-gray-400">{r.git_sha || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <OptimizerTelemetry data={telemetry} />
    </div>
  );
}

export default EvalTrends;
